import { convertHelpers } from "global/common/convert";
import { createRefPath_client, createRefPath_event } from "global/firestoreData/appData/appRefPaths";
import { updateLinkHelpers } from "global/firestoreData/updates/subUpdates/fsUpdate_dataLInks";
import _ from 'lodash';
import { useContext, useMemo, useState } from "react";
import { DemoContext } from "../DemoContext";
import { PapsContext } from "../PapsContext";
import { RegDataContext } from "../RegDataContext";
import { CurrentSettingsContext } from "../SettingsDataContext";
import { useCachedData } from "./useCachedData";
import { _cacheGroupTypes } from "./useTimestamp";
import { findDuplicates } from "global/common/dataDiff";
import { useAppUserAccess } from "./useAppUserAccess";

const eventInfoCallTypes = {
  event: 'event',
  appDataDocuments: 'appDataDocuments',
  appGlobalData: 'appGlobalData',
  appZoneData: 'appZoneData',
};

export const useEventInfo = () => {

  const appUserAccess = useAppUserAccess()
  const { isSuperAdminDeveloper } = appUserAccess ?? {}

  const papsContext = useContext(PapsContext);
  const { paps_state } = papsContext
  const { pathViews } = paps_state ?? {}
  const { events: eventKey } = pathViews ?? {}

  const currentSettingsContext = useContext(CurrentSettingsContext)
  const { currentSettings_state } = currentSettingsContext ?? {}
  const { aps_global, aps_viewItems } = _.get(currentSettings_state, 'gvviSettings', {});
  const { display: display_global, } = aps_global ?? {}
  const { removeTrailingNumberTexts } = display_global ?? {}

  const demoContext = useContext(DemoContext);
  const { demo_state } = demoContext ?? {}
  const { demoPreview, demoOn } = demo_state ?? {}
  const { appDataDocuments: appDd_demo, projectData: projectData_demo } = demoPreview ?? {}
  const { eventData: eventData_demo } = projectData_demo ?? {}

  const regDataContext = useContext(RegDataContext);
  const { regData_state } = regDataContext ?? {}
  const { regInfo, isRegDemo } = regData_state ?? {}
  const { appDataDocuments: appDd_reg, eventData: eventData_reg } = regInfo ?? {}

  const [appDataDocuments, setAppDataDocuments] = useState()
  const [eventData, setEventData] = useState()
  const [appGlobalData, setAppGlobalData] = useState()
  const [appZoneData, setAppZoneData] = useState()
  const [eventDups, setEventDups] = useState()

  const postProcess = (mergedData) => {
    if (appDataDocuments && mergedData?.appDataDocuments) {
      mergedData.appDataDocuments = _.mergeWith(
        {},
        appDataDocuments,
        mergedData.appDataDocuments,
        (objValue, srcValue) => {
          if (_.isArray(srcValue)) {
            return srcValue; // src (mergedData) wins for arrays
          }
        }
      );
    }
    return mergedData;
  };

  useCachedData({
    cache_groupType: _cacheGroupTypes.eventInfo,
    cache_key: eventKey,
    fetchers: [
      { key: eventInfoCallTypes.event, getConfigFn: getConfig_event },
      { key: eventInfoCallTypes.appDataDocuments, getConfigFn: getConfig_appDataDocuments },
      { key: eventInfoCallTypes.appGlobalData, getConfigFn: getConfig_eventGlobals },
      { key: eventInfoCallTypes.appZoneData, getConfigFn: getConfig_eventZones },
    ],
    postProcess,
    updateLocalState: (data) => {
      const eventData = _.cloneDeep(data?.[eventInfoCallTypes.event] ?? {});
      const appDataDocuments = _.cloneDeep(data?.[eventInfoCallTypes.appDataDocuments] ?? {});
      const appGlobalData = _.cloneDeep(data?.[eventInfoCallTypes.appGlobalData] ?? {});
      const appZoneData = _.cloneDeep(data?.[eventInfoCallTypes.appZoneData] ?? {});

      // side effects only if data exists
      if (!_.isEmpty(appDataDocuments)) {
        updateLinkHelpers.ammendAppDataLinks(appDataDocuments, aps_viewItems);
        _.forEach(appDataDocuments, (v) => {
          convertHelpers.createItemKeys(v);
          if (removeTrailingNumberTexts) {
            convertHelpers.cleanPropValues(v)
          }
        });
      }

      if (isSuperAdminDeveloper) {
        const dups = findDuplicates(appDataDocuments)
        setEventDups(dups)
        console.log('dups', dups)
      }

      if (isRegDemo && appDd_reg && eventData_reg) {
        setEventData(eventData_reg);
        setAppDataDocuments(appDd_reg);
      } else if (demoOn && appDd_demo) {
        setAppDataDocuments(appDd_demo);
      } else {
        setEventData(eventData);
        setAppDataDocuments(appDataDocuments);
        setAppGlobalData(appGlobalData);
        setAppZoneData(appZoneData);
      }
    }
  })

  // ------------------- Memoized stable return -------------------
  const eventInfo = useMemo(
    () => ({ eventData, appDataDocuments, appGlobalData, appZoneData, eventDups }),
    [eventData, appDataDocuments, appGlobalData, appZoneData, eventDups]
  );

  return { eventInfo };

};

/**
 *  ['events', pathViews.events]
 * @param {*} pathViews 
 * @param {*} callback 
 * @returns 
 */
const getConfig_event = (pathViews, callback) => ({
  refPath: createRefPath_client(pathViews, ['events', pathViews.events]),
  callback,
  opts: { ignoreId: true, returnFirstObject: true, listen: true },
});

/**
 * ['_documents']
 * @param {*} pathViews 
 * @param {*} callback 
 * @returns 
 */
const getConfig_appDataDocuments = (pathViews, callback) => ({
  refPath: createRefPath_event(pathViews, ['_documents']),
  opts: { listen: true, ignoreId: true, listen: true },
  callback,
});

const getConfig_eventGlobals = (pathViews, callback) => ({
  refPath: createRefPath_event(pathViews, ['_globals']),
  callback,
  opts: { ignoreId: true, returnFirstObject: false, listen: true },
});

const getConfig_eventZones = (pathViews, callback) => ({
  refPath: createRefPath_event(pathViews, ['_zones']),
  callback,
  opts: { ignoreId: true, returnFirstObject: false, listen: true },
});


export const cleanDataDeep = (input) => {
  // Handle null/undefined immediately
  if (input === null || input === undefined) return undefined;

  // Handle arrays
  if (Array.isArray(input)) {
    const cleanedArray = input
      .map(item => cleanDataDeep(item))
      .filter(item =>
        item !== undefined &&
        item !== null &&
        item !== '' &&
        !(Array.isArray(item) && item.length === 0)
      );

    return cleanedArray.length ? cleanedArray : undefined;
  }

  // Handle objects
  if (typeof input === 'object') {
    const obj = input;

    for (const key in obj) {
      const cleaned = cleanDataDeep(obj[key]);

      if (
        cleaned === undefined ||
        cleaned === null ||
        cleaned === '' ||
        (Array.isArray(cleaned) && cleaned.length === 0)
      ) {
        delete obj[key];
      } else {
        obj[key] = cleaned;
      }
    }

    return Object.keys(obj).length ? obj : undefined;
  }

  // Primitive values (string, number, boolean)
  if (input === '') return undefined;

  return input;
};

// const cleanPropValues = (obj) => {
//   if (_.isArray(obj)) {
//     _.forEach(obj, (val, i) => {
//       if (_.isString(val)) {
//         obj[i] = val.replace(/\s-\s\d+$/, '');
//       } else {
//         cleanPropValues(val);
//       }
//     });
//     return;
//   }

//   if (_.isObject(obj)) {
//     _.forOwn(obj, (val, key) => {
//       if (_.isString(val)) {
//         obj[key] = val.replace(/\s-\s\d+$/, '');
//       } else {
//         cleanPropValues(val);
//       }
//     });
//   }
// };